import {
  Stack,
  Select,
  Checkbox,
  DEFAULT_THEME,
  Group,
  Button,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSuspenseQuery, useQuery, useMutation } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { samplingApi } from '../../data-access/apis/sampling';
import { useSamplingQueries } from '../../data-access/queries/sampling';

export interface ManageCarbonSamplingPlanFormProps {
  analysisType: string;
  fieldTeledecId: number | null;
  ilotId: number | null;
  onBack?: () => void;
  planId: number;
}

export const ManageCarbonSamplingPlanForm: React.FC<
  ManageCarbonSamplingPlanFormProps
> = ({ analysisType, fieldTeledecId, ilotId, onBack, planId }) => {
  const { t } = useTranslation();
  const samplingQueries = useSamplingQueries();
  const plan = useSuspenseQuery(samplingQueries.plans.carbon.debug(planId));
  const formValues = useQuery(samplingQueries.plans.values({}));

  const update = useMutation({
    mutationFn: samplingApi.plans.density.update,
  });

  const form = useForm({
    initialValues: {
      nbr_points: plan.data.nbr_points.toString(),
      zoning_method: plan.data.zoning_method,
      erosion: plan.data.erosion.toString(),
      restart: false,
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    update.mutate(
      {
        id: planId,
        data: {
          nbr_points: Number(values.nbr_points),
          zoning_method: values.zoning_method,
          erosion: Number(values.erosion),
          restart: values.restart,
        },
      },
      {
        onSuccess: (response) => {
          form.setInitialValues({
            nbr_points: response.nbr_points.toString(),
            zoning_method: response.zoning_method,
            erosion: response.erosion.toString(),
            restart: false,
          });
          form.reset();
        },
      },
    );
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label={t('common.properties.ilot_id')}
          readOnly
          value={ilotId != null ? String(ilotId) : '—'}
        />
        <TextInput
          label={t('common.properties.analysis_type')}
          readOnly
          value={t(`sampling.analysis_types.${analysisType}`, {
            defaultValue: analysisType,
          })}
        />
        {fieldTeledecId != null ? (
          <TextInput
            label={t('common.properties.field_teledec_id')}
            readOnly
            value={String(fieldTeledecId)}
          />
        ) : null}
        <Select
          data={formValues.data?.nbr_points.map(({ value, label }) => ({
            value: value.toString(),
            label,
          }))}
          label={t('common.nombre-de-points')}
          {...form.getInputProps('nbr_points')}
        />
        <Select
          data={formValues.data?.zoning_method.map(({ value, label }) => ({
            value: value.toString(),
            label,
          }))}
          label={t('common.methode-de-zonage')}
          {...form.getInputProps('zoning_method')}
        />
        <Select
          data={formValues.data?.erosion.map(({ value, label }) => ({
            value: value.toString(),
            label,
          }))}
          label={t('common.erosion')}
          {...form.getInputProps('erosion')}
        />
        <Checkbox
          label={t('common.relancer-le-calcul')}
          mt="sm"
          styles={{
            label: {
              fontSize: DEFAULT_THEME.fontSizes.md,
            },
          }}
          {...form.getInputProps('restart', { type: 'checkbox' })}
        />
      </Stack>

      <Group mt="xl">
        <Button type="submit">{t('common.save')}</Button>
        <Button color="red" onClick={onBack} type="button">
          {t('common.back')}
        </Button>
      </Group>
    </form>
  );
};
